import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import {
    LISTA_JUSTIFICATIVAS_FALTA,
    CRIA_JUSTIFICATIVA_FALTA,
    EDITA_JUSTIFICATIVA_FALTA,
    REMOVE_JUSTIFICATIVA_FALTA,
    DESVINCULAR_JUSTIFICATIVA_FALTA,
    CALCULO_JUSTIFICATIVA_FALTA,
    ADICIONA_ANEXOS_JUSTIFICATIVA_FALTA,
    REMOVE_ANEXO_JUSTIFICATIVA_FALTA,
} from "constants/ActionTypes";
import requisicaoApi from 'util/Api';
import {
    listaJustificativasFalta as acListaJustificativas,
    listaJustificativasFaltaSucesso,
    criaJustificativaFaltaSucesso,
    editaJustificativaFaltaSucesso,
    removeJustificativaFaltaSucesso,
    desvincularJustificativaFaltaSucesso,
    calculoJustificativaFalta as acCalculo,
    calculoJustificativaFaltaSucesso,
    adicionaAnexosJustificativaFaltaSucesso,
    removeAnexoJustificativaFaltaSucesso,
    justificativaFaltaErro,
} from "actions/JustificativaFalta";
import { showAuthMessage, showSuccessMessage } from "actions/NotasFaltas";

/*#################################################################################################################*/
/* --------------------------------------------- Helpers ---------------------------------------------------------*/

// Endpoints de justificativa retornam o envelope { sucesso, mensagem, dados }.
const extractMensagem = (error, padrao) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data) return data; // catálogo de tipos: 400 como string simples
    if (data?.mensagem) return data.mensagem;
    if (data?.message) return data.message;
    if (error?.message) return error.message;
    return padrao;
};

const apiListaJustificativas = async (params) => await requisicaoApi.get('/justificativas-falta', { params });

// POST multipart/form-data: campo `justificativa` (JSON string) + `arquivos` (0..N File).
const apiCriaJustificativa = async (dados, arquivos) => {
    const fd = new FormData();
    fd.append('justificativa', JSON.stringify(dados));
    (arquivos || []).forEach(f => fd.append('arquivos', f));
    // Não definir Content-Type manualmente — o browser põe o boundary do multipart.
    return await requisicaoApi.post('/justificativas-falta', fd);
};

// PUT multipart/form-data: campo `justificativa` (JSON string) + `arquivosManterIds`
// (JSON string opcional: ids dos anexos a preservar) + `arquivos` (0..N File novos).
const apiEditaJustificativa = async (id, dados, arquivosManterIds, arquivos) => {
    const fd = new FormData();
    fd.append('justificativa', JSON.stringify(dados));
    if (Array.isArray(arquivosManterIds)) {
        fd.append('arquivosManterIds', JSON.stringify(arquivosManterIds));
    }
    (arquivos || []).forEach(f => fd.append('arquivos', f));
    return await requisicaoApi.put(`/justificativas-falta/${id}`, fd);
};

const apiRemoveJustificativa = async (id) => await requisicaoApi.delete(`/justificativas-falta/${id}`);
const apiDesvincularJustificativa = async (id, componenteEscolaId) => await requisicaoApi.delete(`/justificativas-falta/${id}/disciplinas/${componenteEscolaId}`);
const apiCalculo = async (params) => await requisicaoApi.get('/justificativas-falta/calculo', { params });

const apiAdicionaAnexos = async (id, arquivos) => {
    const fd = new FormData();
    (arquivos || []).forEach(f => fd.append('arquivos', f));
    return await requisicaoApi.post(`/justificativas-falta/${id}/arquivos`, fd);
};

const apiRemoveAnexo = async (id, arquivoId) => await requisicaoApi.delete(`/justificativas-falta/${id}/arquivos/${arquivoId}`);

/*#################################################################################################################*/
/* ------------------------------------------ Funções das Sagas ---------------------------------------------------*/

function* fnListaJustificativas({ payload }) {
    try {
        const { matriculaId, componenteEscolaId, periodoAnoLetivoId } = payload || {};
        if (!matriculaId) {
            yield put(listaJustificativasFaltaSucesso([]));
            return;
        }
        const params = { matriculaId };
        if (componenteEscolaId) params.componenteEscolaId = componenteEscolaId;
        if (periodoAnoLetivoId) params.periodoAnoLetivoId = periodoAnoLetivoId;

        const resp = yield call(apiListaJustificativas, params);
        const dados = resp?.data?.dados || [];
        yield put(listaJustificativasFaltaSucesso(Array.isArray(dados) ? dados : []));
    } catch (error) {
        const msg = extractMensagem(error, 'Erro ao listar justificativas de falta.');
        yield put(justificativaFaltaErro(msg));
        yield put(showAuthMessage(msg));
    }
}

function* fnCriaJustificativa({ payload }) {
    const { dados, arquivos, contexto } = payload || {};
    try {
        const resp = yield call(apiCriaJustificativa, dados, arquivos);
        const envelope = resp?.data || {};
        if (envelope.sucesso === false) {
            yield put(justificativaFaltaErro(envelope.mensagem || 'Erro ao registrar justificativa.'));
            yield put(showAuthMessage(envelope.mensagem || 'Erro ao registrar justificativa.'));
            return;
        }
        yield put(criaJustificativaFaltaSucesso());
        yield put(showSuccessMessage(envelope.mensagem || 'Justificativa registrada.'));
        // Refresh do contexto (modal) e do cálculo do chip (cirúrgico).
        if (contexto) {
            yield put(acListaJustificativas(contexto));
            yield put(acCalculo({ matriculaId: contexto.matriculaId, periodoAnoLetivoId: contexto.periodoAnoLetivoId }));
        }
    } catch (error) {
        const msg = extractMensagem(error, 'Erro ao registrar justificativa.');
        yield put(justificativaFaltaErro(msg));
        yield put(showAuthMessage(msg));
    }
}

function* fnEditaJustificativa({ payload }) {
    const { id, dados, arquivosManterIds, arquivos, contexto } = payload || {};
    try {
        const resp = yield call(apiEditaJustificativa, id, dados, arquivosManterIds, arquivos);
        const envelope = resp?.data || {};
        if (envelope.sucesso === false) {
            yield put(justificativaFaltaErro(envelope.mensagem || 'Erro ao atualizar justificativa.'));
            yield put(showAuthMessage(envelope.mensagem || 'Erro ao atualizar justificativa.'));
            return;
        }
        yield put(editaJustificativaFaltaSucesso());
        yield put(showSuccessMessage(envelope.mensagem || 'Justificativa atualizada.'));
        // Refresh do contexto (modal) e do cálculo do chip — alterar datas/abrangência/disciplinas
        // muda o cálculo de faltas justificadas.
        if (contexto) {
            yield put(acListaJustificativas(contexto));
            yield put(acCalculo({ matriculaId: contexto.matriculaId, periodoAnoLetivoId: contexto.periodoAnoLetivoId }));
        }
    } catch (error) {
        const msg = extractMensagem(error, 'Erro ao atualizar justificativa.');
        yield put(justificativaFaltaErro(msg));
        yield put(showAuthMessage(msg));
    }
}

function* fnRemoveJustificativa({ payload }) {
    const { id, contexto } = payload || {};
    try {
        const resp = yield call(apiRemoveJustificativa, id);
        const envelope = resp?.data || {};
        if (envelope.sucesso === false) {
            yield put(justificativaFaltaErro(envelope.mensagem || 'Erro ao remover justificativa.'));
            yield put(showAuthMessage(envelope.mensagem || 'Erro ao remover justificativa.'));
            return;
        }
        yield put(removeJustificativaFaltaSucesso());
        yield put(showSuccessMessage(envelope.mensagem || 'Justificativa removida.'));
        if (contexto) {
            yield put(acListaJustificativas(contexto));
            yield put(acCalculo({ matriculaId: contexto.matriculaId, periodoAnoLetivoId: contexto.periodoAnoLetivoId }));
        }
    } catch (error) {
        const msg = extractMensagem(error, 'Erro ao remover justificativa.');
        yield put(justificativaFaltaErro(msg));
        yield put(showAuthMessage(msg));
    }
}

function* fnDesvincularJustificativa({ payload }) {
    const { id, componenteEscolaId, contexto } = payload || {};
    try {
        const resp = yield call(apiDesvincularJustificativa, id, componenteEscolaId);
        const envelope = resp?.data || {};
        if (envelope.sucesso === false) {
            yield put(justificativaFaltaErro(envelope.mensagem || 'Erro ao desvincular disciplina.'));
            yield put(showAuthMessage(envelope.mensagem || 'Erro ao desvincular disciplina.'));
            return;
        }
        yield put(desvincularJustificativaFaltaSucesso());
        yield put(showSuccessMessage(envelope.mensagem || 'Disciplina desvinculada da justificativa.'));
        if (contexto) {
            yield put(acListaJustificativas(contexto));
            yield put(acCalculo({ matriculaId: contexto.matriculaId, periodoAnoLetivoId: contexto.periodoAnoLetivoId }));
        }
    } catch (error) {
        const msg = extractMensagem(error, 'Erro ao desvincular disciplina da justificativa.');
        yield put(justificativaFaltaErro(msg));
        yield put(showAuthMessage(msg));
    }
}

function* fnCalculoJustificativa({ payload }) {
    try {
        const { matriculaId, periodoAnoLetivoId } = payload || {};
        if (!matriculaId) return;
        const params = { matriculaId };
        if (periodoAnoLetivoId) params.periodoAnoLetivoId = periodoAnoLetivoId;

        const resp = yield call(apiCalculo, params);
        const dados = resp?.data?.dados || {};
        const porComponente = dados.porComponente || {};
        yield put(calculoJustificativaFaltaSucesso({ matriculaId, porComponente }));
    } catch (error) {
        // Falha no recálculo não bloqueia o usuário; apenas avisa.
        yield put(showAuthMessage(extractMensagem(error, 'Erro ao recalcular justificativas.')));
    }
}

function* fnAdicionaAnexos({ payload }) {
    const { id, arquivos, contexto } = payload || {};
    try {
        const resp = yield call(apiAdicionaAnexos, id, arquivos);
        const envelope = resp?.data || {};
        if (envelope.sucesso === false) {
            yield put(justificativaFaltaErro(envelope.mensagem || 'Erro ao adicionar anexo(s).'));
            yield put(showAuthMessage(envelope.mensagem || 'Erro ao adicionar anexo(s).'));
            return;
        }
        yield put(adicionaAnexosJustificativaFaltaSucesso());
        yield put(showSuccessMessage(envelope.mensagem || 'Anexo(s) adicionado(s).'));
        // Refresh apenas da lista do modal (anexos não alteram o cálculo de faltas).
        if (contexto) {
            yield put(acListaJustificativas(contexto));
        }
    } catch (error) {
        const msg = extractMensagem(error, 'Erro ao adicionar anexo(s).');
        yield put(justificativaFaltaErro(msg));
        yield put(showAuthMessage(msg));
    }
}

function* fnRemoveAnexo({ payload }) {
    const { id, arquivoId, contexto } = payload || {};
    try {
        const resp = yield call(apiRemoveAnexo, id, arquivoId);
        const envelope = resp?.data || {};
        if (envelope.sucesso === false) {
            yield put(justificativaFaltaErro(envelope.mensagem || 'Erro ao remover anexo.'));
            yield put(showAuthMessage(envelope.mensagem || 'Erro ao remover anexo.'));
            return;
        }
        yield put(removeAnexoJustificativaFaltaSucesso());
        yield put(showSuccessMessage(envelope.mensagem || 'Anexo removido.'));
        if (contexto) {
            yield put(acListaJustificativas(contexto));
        }
    } catch (error) {
        const msg = extractMensagem(error, 'Erro ao remover anexo.');
        yield put(justificativaFaltaErro(msg));
        yield put(showAuthMessage(msg));
    }
}

/*#################################################################################################################*/
/* --------------------------------------------- Watchers --------------------------------------------------------*/

export function* listaJustificativasFaltaWatcher() {
    yield takeEvery(LISTA_JUSTIFICATIVAS_FALTA, fnListaJustificativas);
}

export function* criaJustificativaFaltaWatcher() {
    yield takeEvery(CRIA_JUSTIFICATIVA_FALTA, fnCriaJustificativa);
}

export function* editaJustificativaFaltaWatcher() {
    yield takeEvery(EDITA_JUSTIFICATIVA_FALTA, fnEditaJustificativa);
}

export function* removeJustificativaFaltaWatcher() {
    yield takeEvery(REMOVE_JUSTIFICATIVA_FALTA, fnRemoveJustificativa);
}

export function* desvincularJustificativaFaltaWatcher() {
    yield takeEvery(DESVINCULAR_JUSTIFICATIVA_FALTA, fnDesvincularJustificativa);
}

export function* calculoJustificativaFaltaWatcher() {
    yield takeEvery(CALCULO_JUSTIFICATIVA_FALTA, fnCalculoJustificativa);
}

export function* adicionaAnexosJustificativaFaltaWatcher() {
    yield takeEvery(ADICIONA_ANEXOS_JUSTIFICATIVA_FALTA, fnAdicionaAnexos);
}

export function* removeAnexoJustificativaFaltaWatcher() {
    yield takeEvery(REMOVE_ANEXO_JUSTIFICATIVA_FALTA, fnRemoveAnexo);
}

export default function* rootSaga() {
    yield all([
        fork(listaJustificativasFaltaWatcher),
        fork(criaJustificativaFaltaWatcher),
        fork(editaJustificativaFaltaWatcher),
        fork(removeJustificativaFaltaWatcher),
        fork(desvincularJustificativaFaltaWatcher),
        fork(calculoJustificativaFaltaWatcher),
        fork(adicionaAnexosJustificativaFaltaWatcher),
        fork(removeAnexoJustificativaFaltaWatcher),
    ]);
}
